import { Link } from "react-router-dom";
import HeroSlider from "@/pages/home/components/HeroSlider";
import CategoryEntries from "@/pages/home/components/CategoryEntries";
import MovieCard from "@/components/feature/MovieCard";
import { mediaList } from "@/mocks/media";
import { useSeo } from "@/hooks/useSeo";

export default function Home() {
  useSeo({
    title: "我的家庭教师_热门电影电视剧影视频道",
    description:
      "我的家庭教师网站为您提供丰富的影视资源，包括电影、电视剧等多类型视频内容，满足您全方位的观影需求。",
    keywords:
      "我的家庭教师,电影,电视剧,影视视频,家庭教师影视,视频播放,影视资源,影视大全",
    path: "/",
  });

  const featured = mediaList.filter((item) => item.featured).slice(0, 6);
  const latest = [...mediaList]
    .sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1))
    .slice(0, 8);
  const popular = [...mediaList]
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, 8);

  return (
    <div>
      <HeroSlider items={featured} />

      <CategoryEntries />

      <section className="mx-auto max-w-[1280px] px-4 pt-14 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-wide text-foreground-950">
              最新上线
            </h2>
            <p className="mt-1.5 text-sm text-foreground-600">
              按更新时间精选，追新剧集与院线新片一网打尽
            </p>
          </div>
          <Link
            to="/search"
            className="flex items-center gap-1 whitespace-nowrap text-sm text-accent-400 transition-colors hover:text-accent-300"
          >
            查看更多
            <i className="ri-arrow-right-s-line text-lg"></i>
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {latest.map((item) => (
            <MovieCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 pt-14 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-wide text-foreground-950">
              热门推荐
            </h2>
            <p className="mt-1.5 text-sm text-foreground-600">
              依据播放热度与评分推荐，看看大家都在追什么
            </p>
          </div>
          <Link
            to={`/category/${encodeURIComponent("电影")}`}
            className="flex items-center gap-1 whitespace-nowrap text-sm text-accent-400 transition-colors hover:text-accent-300"
          >
            进入影视库
            <i className="ri-arrow-right-s-line text-lg"></i>
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {popular.map((item) => (
            <MovieCard key={item.id} item={item} showActors />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6">
        <div className="grid grid-cols-1 items-center gap-8 rounded-lg border border-background-200/70 bg-gradient-to-br from-primary-950 to-background-100 p-6 md:p-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-accent-500/40 bg-accent-500/10 px-3 py-1 text-xs font-medium text-accent-400">
              <i className="ri-home-heart-line text-sm"></i>
              关于我的家庭教师
            </span>
            <h2 className="mt-4 font-heading text-2xl font-semibold tracking-wide text-foreground-950 md:text-3xl">
              为每个家庭，挑一部好片
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              「我的家庭教师」是一个专注家庭观影场景的影视内容门户，聚合电影、电视剧、综艺等多元资源。
              我们以清晰的内容分类、多维的筛选排序与详尽的影视资料，帮助您更快找到想看的作品，
              让每一次家庭观影都更轻松、更有温度。
            </p>
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div>
                <p className="font-heading text-2xl font-bold text-accent-400">20+</p>
                <p className="mt-1 text-xs text-foreground-600">精选影视作品</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-accent-400">3</p>
                <p className="mt-1 text-xs text-foreground-600">核心内容分类</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-accent-400">100%</p>
                <p className="mt-1 text-xs text-foreground-600">资料真实对应</p>
              </div>
            </div>
            <Link
              to="/about"
              className="mt-7 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600"
            >
              了解我们
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img
              src="https://readdy.ai/api/search-image?query=Cozy%20family%20living%20room%20watching%20a%20movie%20together%20at%20night%20on%20a%20big%20screen%2C%20warm%20amber%20lamp%20glow%20with%20deep%20navy%20tones%2C%20comfortable%20sofa%20and%20blanket%2C%20cinematic%20cozy%20atmosphere%2C%20no%20text%2C%20no%20letters%2C%20high%20detail%20editorial%20photography&width=600&height=400&seq=intro-a-01&orientation=landscape"
              alt="家庭观影场景"
              title="我的家庭教师 家庭观影"
              className="h-40 w-full rounded-lg object-cover object-top md:h-52"
            />
            <img
              src="https://readdy.ai/api/search-image?query=Cinema%20seat%20rows%20in%20a%20dark%20premium%20movie%20theater%20with%20a%20glowing%20screen%2C%20deep%20navy%20blue%20environment%20and%20warm%20orange%20accent%20lighting%2C%20cinematic%20mood%2C%20no%20text%2C%20no%20letters%2C%20high%20detail%20editorial%20photography&width=600&height=400&seq=intro-b-02&orientation=landscape"
              alt="影院观影氛围"
              title="我的家庭教师 影视资源"
              className="mt-6 h-40 w-full rounded-lg object-cover object-top md:h-52"
            />
          </div>
        </div>
      </section>
    </div>
  );
}